import { useState, useEffect, useCallback } from 'react';
import { Network, MessageSquare, ChevronDown, Loader as Loader2 } from 'lucide-react';
import { useApi } from '../../lib/useApi';
import type { Account, UseCaseRecord, UseCaseItem, Contact, ContactUseCaseScore, OutboundPriority, ValueStatements, AccountDepartment, TargetPersona } from '../../types';
import {
  extractPersonaTargetsFromResearch,
  computeUseCaseRelevanceForContact,
  detectMissingPersonas,
  type ResearchedPersonaTargets,
  type MissingPersona,
} from '../../lib/orgChartUtils';
import LoadingSpinner from '../common/LoadingSpinner';
import OrgChartSubTab from './execute/OrgChartSubTab';
import ContactOutreachSubTab from './execute/ContactOutreachSubTab';
import TriggerContextSelector from './triggers/TriggerContextSelector';
import type { SelectedTriggerContext } from './triggers/TriggerContextSelector';

interface Props {
  account: Account;
  deepContactId?: string | null;
}

interface ExecuteContextResponse {
  target_personas: TargetPersona[];
  target_personas_customized?: boolean;
  use_case: UseCaseRecord | null;
  vendor_name: string;
  value_statements_summary: ValueStatements['summary'] | null;
  departments: AccountDepartment[];
}

const EXECUTE_SUBTABS = [
  { id: 'org-chart', label: 'Org Chart', icon: Network },
  { id: 'outreach', label: 'Contact Outreach', icon: MessageSquare },
];

export default function ExecuteTab({ account, deepContactId }: Props) {
  const { apiGet, apiPut } = useApi();
  const [activeSubTab, setActiveSubTab] = useState(deepContactId ? 'outreach' : 'org-chart');
  const [useCaseRecord, setUseCaseRecord] = useState<UseCaseRecord | null>(null);
  const [selectedUseCaseIdx, setSelectedUseCaseIdx] = useState<number>(0);
  const [vendorName, setVendorName] = useState('');
  const [loading, setLoading] = useState(true);

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [ucScores, setUcScores] = useState<Map<string, ContactUseCaseScore[]>>(new Map());
  const [scoringInProgress, setScoringInProgress] = useState(false);
  const [researchedPersonas, setResearchedPersonas] = useState<ResearchedPersonaTargets | null>(null);
  const [missingPersonas, setMissingPersonas] = useState<MissingPersona[]>([]);

  const [departments, setDepartments] = useState<AccountDepartment[]>([]);

  const [outreachContactId, setOutreachContactId] = useState<string | null>(deepContactId || null);
  const [selectedTrigger, setSelectedTrigger] = useState<SelectedTriggerContext | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    const [ctxRes, contactRes, scoresRes] = await Promise.all([
      apiGet<ExecuteContextResponse>(`/v1/account/${account.id}/execute-context`).catch((): ExecuteContextResponse => ({
        use_case: null,
        vendor_name: '',
        target_personas: [] as TargetPersona[],
        value_statements_summary: null,
        departments: [] as AccountDepartment[],
      })),
      apiGet<{ contacts: Contact[] }>(`/v1/account/${account.id}/contacts`).catch(() => ({ contacts: [] as Contact[] })),
      apiGet<{ scores: ContactUseCaseScore[] }>(`/v1/account/${account.id}/contact-scores`).catch(() => ({ scores: [] as ContactUseCaseScore[] })),
    ]);
    if (ctxRes.use_case) setUseCaseRecord(ctxRes.use_case);
    const localContacts = contactRes.contacts || [];
    setContacts(localContacts);
    setVendorName(ctxRes.vendor_name || '');
    setDepartments(ctxRes.departments || []);

    {
      const summary = ctxRes.value_statements_summary;
      const organisationPersonas = ctxRes.target_personas || [];
      setResearchedPersonas({
        // An empty list is authoritative only when an admin saved it; an organisation that has not
        // established personas yet (or a failed load) falls back to the account's researched personas.
        organisationPersonas: organisationPersonas.length || ctxRes.target_personas_customized ? organisationPersonas : undefined,
        personaImpactMap: summary?.persona_impact_map || [],
        valueStatements: summary?.value_statements || [],
      });
    }

    if (scoresRes.scores) {
      const scoreMap = new Map<string, ContactUseCaseScore[]>();
      for (const s of scoresRes.scores) {
        const key = s.use_case_title;
        if (!scoreMap.has(key)) scoreMap.set(key, []);
        scoreMap.get(key)!.push(s);
      }
      setUcScores(scoreMap);
    }

    setLoading(false);
  }, [account.id, apiGet]);

  useEffect(() => { loadData(); }, [loadData]);

  const useCases = useCaseRecord?.use_case_data || [];
  const sortedUseCases = [...useCases].sort((a, b) => a.rank - b.rank);
  const selectedUseCase = sortedUseCases[selectedUseCaseIdx] || null;

  const currentUcScores = selectedUseCase
    ? (ucScores.get(selectedUseCase.title) || [])
    : [];

  const computeAllScores = useCallback(async (contactList: Contact[], ucList: UseCaseItem[], research: ResearchedPersonaTargets | null) => {
    if (contactList.length === 0 || ucList.length === 0) return;
    setScoringInProgress(true);

    const allRows: Omit<ContactUseCaseScore, 'id' | 'created_at' | 'updated_at' | 'priority_override' | 'override_justification'>[] = [];

    for (const uc of ucList) {
      const targets = extractPersonaTargetsFromResearch(research, uc);
      for (const contact of contactList) {
        const result = computeUseCaseRelevanceForContact(contact, uc, targets);
        allRows.push({
          contact_id: contact.id,
          account_id: account.id,
          use_case_title: uc.title,
          use_case_rank: uc.rank,
          persona_match: result.persona_match,
          persona_match_score: result.persona_match_score,
          use_case_relevance_score: result.use_case_relevance_score,
          outbound_priority: result.outbound_priority,
          priority_rationale: result.priority_rationale,
        });
      }
    }

    const { scores: freshScores } = await apiPut<{ scores: ContactUseCaseScore[] }>(
      `/v1/account/${account.id}/contact-scores`,
      { scores: allRows },
    );

    if (freshScores) {
      const scoreMap = new Map<string, ContactUseCaseScore[]>();
      for (const s of freshScores) {
        if (!scoreMap.has(s.use_case_title)) scoreMap.set(s.use_case_title, []);
        scoreMap.get(s.use_case_title)!.push(s);
      }
      setUcScores(scoreMap);
    }

    setScoringInProgress(false);
  }, [account.id, apiPut]);

  const needsScoring = contacts.length > 0 && sortedUseCases.length > 0 && ucScores.size === 0;

  useEffect(() => {
    if (needsScoring && !scoringInProgress) {
      computeAllScores(contacts, sortedUseCases, researchedPersonas);
    }
  }, [needsScoring, scoringInProgress, contacts, sortedUseCases, computeAllScores, researchedPersonas]);

  useEffect(() => {
    if (!selectedUseCase || contacts.length === 0 || currentUcScores.length === 0) {
      setMissingPersonas([]);
      return;
    }
    const targets = extractPersonaTargetsFromResearch(researchedPersonas, selectedUseCase);
    const scoreMap = new Map<string, { persona_match_score: number; persona_match: string }>();
    for (const s of currentUcScores) {
      scoreMap.set(s.contact_id, { persona_match_score: s.persona_match_score, persona_match: s.persona_match });
    }
    setMissingPersonas(detectMissingPersonas(targets, contacts, scoreMap));
  }, [selectedUseCase, contacts, currentUcScores, researchedPersonas]);

  const handleContactsChanged = useCallback(async (updatedContacts: Contact[]) => {
    setContacts(updatedContacts);
    if (sortedUseCases.length > 0) {
      await computeAllScores(updatedContacts, sortedUseCases, researchedPersonas);
    }
  }, [sortedUseCases, computeAllScores, researchedPersonas]);

  const handleGoToMessaging = useCallback((contactId: string) => {
    setOutreachContactId(contactId);
    setActiveSubTab('outreach');
  }, []);

  const handlePriorityOverride = useCallback(async (scoreId: string, priority: OutboundPriority | null, justification: string) => {
    await apiPut(`/v1/contact-scores/${scoreId}`, {
      priority_override: priority,
      override_justification: justification,
    });

    setUcScores(prev => {
      const next = new Map(prev);
      for (const [key, scores] of next) {
        const updated = scores.map(s =>
          s.id === scoreId
            ? { ...s, priority_override: priority, override_justification: justification }
            : s
        );
        next.set(key, updated);
      }
      return next;
    });
  }, [apiPut]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex max-w-full gap-1 overflow-x-auto border-b border-app-line">
          {EXECUTE_SUBTABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveSubTab(tab.id);
                if (tab.id !== 'outreach') setOutreachContactId(null);
              }}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition ${
                activeSubTab === tab.id
                  ? 'border-forest-600 text-forest-700'
                  : 'border-transparent text-app-muted hover:text-app-ink hover:border-app-line-strong'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {sortedUseCases.length > 0 && (
          <div className="flex min-w-0 items-center gap-2 sm:flex-shrink-0">
            {scoringInProgress && (
              <div className="flex items-center gap-1.5 text-xs text-forest-600">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Scoring...</span>
              </div>
            )}
            <div className="relative min-w-0 flex-1 sm:flex-none">
              <select
                value={selectedUseCaseIdx}
                onChange={e => setSelectedUseCaseIdx(Number(e.target.value))}
                className="w-full max-w-full appearance-none rounded-lg border border-app-line bg-app-surface px-3 py-1.5 pr-8 text-xs font-medium text-app-ink focus:border-forest-500 focus:outline-none focus:ring-2 focus:ring-forest-500 sm:w-auto sm:max-w-[260px]"
              >
                {sortedUseCases.map((uc, idx) => (
                  <option key={idx} value={idx}>
                    #{uc.rank} {uc.title}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-app-muted pointer-events-none" />
            </div>
          </div>
        )}
      </div>

      {activeSubTab === 'org-chart' && (
        <OrgChartSubTab
          account={account}
          contacts={contacts}
          departments={departments}
          selectedUseCase={selectedUseCase}
          ucScores={currentUcScores}
          scoringInProgress={scoringInProgress}
          missingPersonas={missingPersonas}
          researchedPersonas={researchedPersonas}
          selectedContactId={selectedContactId}
          onContactsChanged={handleContactsChanged}
          onGoToMessaging={handleGoToMessaging}
          onPriorityOverride={handlePriorityOverride}
          onContactSelect={setSelectedContactId}
        />
      )}

      {activeSubTab === 'outreach' && (
        <>
          <TriggerContextSelector
            account={account}
            selectedUseCase={selectedUseCase}
            selectedTrigger={selectedTrigger}
            onSelect={setSelectedTrigger}
          />
          <ContactOutreachSubTab
            account={account}
            contacts={contacts}
            selectedUseCase={selectedUseCase}
            ucScores={currentUcScores}
            vendorName={vendorName}
            preSelectedContactId={outreachContactId}
            triggerContext={selectedTrigger}
          />
        </>
      )}
    </div>
  );
}
